import { initializeApp, getApps, getApp } from 'firebase/app';
import { getDatabase, ref, onValue, off, Database } from 'firebase/database';
import { 
  getAuth, 
  onAuthStateChanged, 
  GoogleAuthProvider, 
  signInWithPopup,
  OAuthProvider,
  FacebookAuthProvider,
  signOut as firebaseSignOut,
  User
} from 'firebase/auth';

// Configuración de Firebase para Web
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
};

// Inicializar Firebase
let app:any;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApp();
}

const auth = getAuth(app);

export const getFirebaseApp = () => {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
};

// --- Proveedores de Autenticación ---

// Google
const googleProvider = new GoogleAuthProvider();

// Apple
const appleProvider = new OAuthProvider('apple.com');

// Facebook
const facebookProvider = new FacebookAuthProvider();

// --- Funciones de Autenticación ---

/**
 * Inicia sesión con Google usando popup.
 */
const signInWithGoogle = async (): Promise<User | null> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error: any) {
    console.error("Error al iniciar sesión con Google:", error.message);
    throw error;
  }
};

/**
 * Inicia sesión con Apple usando popup.
 * Solo funciona en entornos seguros (HTTPS).
 */
const signInWithApple = async (): Promise<User | null> => {
  try {
    const result = await signInWithPopup(auth, appleProvider);
    return result.user;
  } catch (error: any) {
    console.error("Error al iniciar sesión con Apple:", error.message);
    throw error;
  }
};

/**
 * Inicia sesión con Facebook usando popup.
 */
const signInWithFacebook = async (): Promise<User | null> => {
  try {
    const result = await signInWithPopup(auth, facebookProvider);
    return result.user;
  } catch (error: any) {
    console.error("Error al iniciar sesión con Facebook:", error.message);
    throw error;
  }
};

/**
 * Cierra la sesión del usuario actual.
 */
const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Error al cerrar sesión:", error);
  }
};

/**
 * Observador que notifica cambios en el estado de autenticación.
 * @param callback La función a llamar cuando el estado de auth cambia.
 * @returns Una función para desuscribirse del observador.
 */
const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export const authService = {
  signInWithGoogle,
  signInWithApple,
  signInWithFacebook,
  signOut,
  onAuthChange,
  auth,
}; 

// --- Realtime Database helpers ---

export const getRealtimeDb = (): Database => {
  const appInstance = getFirebaseApp();
  return getDatabase(appInstance);
};

export const subscribeToOrderUpserts = (
  userKey: string,
  onUpsert: (data: any) => void
): (() => void) => {
  const db = getRealtimeDb();
  const upsertRef = ref(db, `users/${userKey}/orders/upsert`);
  
  console.log('🔥 Firebase - Suscribiendo al nodo:', `users/${userKey}/orders/upsert`);
  console.log('🔥 Firebase - Referencia del nodo:', upsertRef);

  const handler = (snapshot: any) => {
    console.log('🔥 Firebase - Evento recibido en nodo:', `users/${userKey}/orders/upsert`);
    console.log('🔥 Firebase - Snapshot completo:', snapshot);
    console.log('🔥 Firebase - Snapshot key:', snapshot.key);
    console.log('🔥 Firebase - Snapshot exists:', snapshot.exists());
    
    const value = snapshot.val();
    console.log('🔥 Firebase - Valor del snapshot:', value);
    
    if (!value) {
      console.log('🔥 Firebase - Snapshot sin valor, ignorando');
      return;
    }
    
    console.log('🔥 Firebase - Llamando callback onUpsert con valor:', value);
    // El backend hace update (no acumula): emitir el valor actual
    onUpsert(value);
  };

  console.log('🔥 Firebase - Configurando listener onValue...');
  onValue(upsertRef, handler, (error) => {
    console.error('🔥 Firebase - Error en listener onValue:', error);
  });

  console.log('🔥 Firebase - Listener configurado exitosamente');

  // Unsubscribe
  return () => {
    console.log('🔥 Firebase - Desuscribiendo del nodo:', `users/${userKey}/orders/upsert`);
    off(upsertRef, 'value', handler as any);
  };
};